import { CommandPalette } from "@app/components/command_palette/CommandPalette";
import { DEV_MODE_ACTIVE } from "@app/components/dev/devModeConstants";
import { isAdminNavigationRoute } from "@app/components/navigation/config";
import { useDesktopNavigation } from "@app/components/navigation/DesktopNavigationContext";
import { Navigation } from "@app/components/navigation/Navigation";
import { SettingsModal } from "@app/components/navigation/SettingsModal";
import { SubscriptionEndBanner } from "@app/components/navigation/TrialBanner";
import { useAppLayout } from "@app/components/sparkle/AppLayoutContext";
import { AppLayoutTitle } from "@app/components/sparkle/AppLayoutTitle";
import { useAppKeyboardShortcuts } from "@app/hooks/useAppKeyboardShortcuts";
import { useDocumentScrollMode } from "@app/hooks/useDocumentScrollMode";
import { useDocumentTitle } from "@app/hooks/useDocumentTitle";
import { useHashParam } from "@app/hooks/useHashParams";
import { useAuth, useWorkspace } from "@app/lib/auth/AuthContext";
import { brandPageTitle } from "@app/lib/brand";
import { MOBILE_DOCUMENT_SCROLL_CLASSES } from "@app/lib/documentScrollLayoutClasses";
import { useAppRouter } from "@app/lib/platform";
import { useIsMobile } from "@app/lib/swr/useIsMobile";
import { getHomeRoute } from "@app/lib/utils/router";
import { FULL_SCREEN_HASH_PARAM } from "@app/types/conversation_side_panel";
import { isAdmin } from "@app/types/user";
import { cn } from "@dust-tt/sparkle";
import type React from "react";
import { lazy, Suspense } from "react";

// Lazy-load the dev panel only when dev mode is active.
// The module is never fetched/parsed/executed when dev mode is off.
const DevFeatureFlagPanel = DEV_MODE_ACTIVE
  ? lazy(() =>
      // Dynamic import is necessary here: the dev panel must not be bundled
      // in the main chunk — it should only load when dev mode is active.
      import("@app/components/dev/DevFeatureFlagPanel").then((m) => ({
        default: m.DevFeatureFlagPanel,
      }))
    )
  : null;

interface AppContentLayoutProps {
  children: React.ReactNode;
}

interface AppContentInnerWrapperProps {
  isMobile: boolean;
  isFullScreen: boolean;
  children: React.ReactNode;
}

function AppContentInnerWrapper({
  isMobile,
  isFullScreen,
  children,
}: AppContentInnerWrapperProps) {
  if (isMobile) {
    return children;
  }

  return (
    <div
      className={cn(
        "my-2 mr-2 rounded-xl flex-1 bg-panel-background memoair-canvas-glow border border-border overflow-hidden h-panel",
        isFullScreen && "ml-2"
      )}
      style={{
        boxShadow:
          "0 0 0 0.4px rgba(0, 0, 0, 0.02), 0 0 1px 1px rgba(0, 0, 0, 0.02)",
      }}
    >
      {children}
    </div>
  );
}

export function AppContentLayout({ children }: AppContentLayoutProps) {
  const owner = useWorkspace();
  const router = useAppRouter();
  const isMobile = useIsMobile();
  const { featureFlags, subscription, user } = useAuth();
  const {
    contentClassName,
    contentWidth,
    hasTitle = false,
    hideSidebar = false,
    navChildren,
    pageTitle,
    subNavigation,
    title,
  } = useAppLayout();
  const [fullScreenHash] = useHashParam(FULL_SCREEN_HASH_PARAM);

  const isFullScreen = fullScreenHash === "true";
  const isSettingsRoute =
    router.isReady && isAdminNavigationRoute(router.pathname);

  const closeSettings = () => {
    void router.push(getHomeRoute(owner.sId));
  };

  const hasTitleBar = !!title || hasTitle;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  useDocumentTitle(pageTitle || brandPageTitle(owner.name));
  useAppKeyboardShortcuts(owner);
  const { isNavigationBarOpen, setIsNavigationBarOpen } =
    useDesktopNavigation();

  useDocumentScrollMode(isMobile);

  // One wrapper for both title modes: swapping wrappers would remount the
  // whole page whenever a route toggles its title bar mid-load.
  const pageBody = (
    <div
      className={cn(
        "flex flex-1 flex-col",
        isMobile
          ? MOBILE_DOCUMENT_SCROLL_CLASSES.contentArea
          : cn(
              "min-h-0",
              // The settings modal is shorter than the app panel.
              hasTitleBar || isSettingsRoute ? "h-full" : "h-panel",
              !contentWidth && "overflow-y-auto [scrollbar-gutter:stable]"
            )
      )}
    >
      {hasTitleBar ? contentWidth && title : <AppLayoutTitle />}
      {contentWidth ? (
        <div
          className={cn(
            "flex w-full flex-col items-center",
            isMobile
              ? MOBILE_DOCUMENT_SCROLL_CLASSES.contentArea
              : cn(
                  "overflow-y-auto [scrollbar-gutter:stable]",
                  hasTitleBar ? "min-h-0 flex-1" : "h-full"
                ),
            contentWidth === "centered" ? "pt-4" : "pt-8",
            contentClassName
          )}
        >
          <div
            className={cn(
              "flex w-full grow flex-col px-4 md:px-8",
              contentWidth === "centered" && "max-w-4xl"
            )}
          >
            {children}
          </div>
        </div>
      ) : (
        children
      )}
    </div>
  );

  return (
    <div
      className={cn(
        "flex flex-col",
        isMobile ? MOBILE_DOCUMENT_SCROLL_CLASSES.contentRoot : "h-dvh"
      )}
    >
      <SubscriptionEndBanner
        isAdmin={isAdmin(owner)}
        owner={owner}
        subscription={subscription}
      />
      <div
        className={cn(
          "flex flex-row",
          isMobile
            ? MOBILE_DOCUMENT_SCROLL_CLASSES.contentRow
            : "min-h-0 flex-1"
        )}
      >
        <Navigation
          hideSidebar={hideSidebar}
          isNavigationBarOpen={isNavigationBarOpen}
          setNavigationBarOpen={setIsNavigationBarOpen}
          owner={owner}
          subscription={subscription}
          navChildren={navChildren}
          subNavigation={subNavigation}
          isFullScreen={isFullScreen}
          isMobile={isMobile}
        />
        <main
          className={cn(
            "relative flex w-full flex-1 flex-col text-foreground",
            isMobile
              ? cn(
                  MOBILE_DOCUMENT_SCROLL_CLASSES.contentMain,
                  "memoair-canvas-glow"
                )
              : "h-full overflow-x-hidden bg-app-background"
          )}
        >
          {isSettingsRoute ? (
            <SettingsModal onClose={closeSettings}>{pageBody}</SettingsModal>
          ) : (
            <AppContentInnerWrapper
              isMobile={isMobile}
              isFullScreen={isFullScreen}
            >
              {pageBody}
            </AppContentInnerWrapper>
          )}
        </main>
        <CommandPalette owner={owner} user={user} />
      </div>
      {DevFeatureFlagPanel && (
        <Suspense fallback={null}>
          <DevFeatureFlagPanel serverFlags={featureFlags} />
        </Suspense>
      )}
    </div>
  );
}
